export type RetryOptions = {
  attempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (err: unknown) => boolean;
  signal?: AbortSignal;
  label?: string;
};

const defaultShouldRetry = (err: unknown): boolean => {
  if (err && typeof err === 'object') {
    const anyErr = err as { status?: number; name?: string; code?: string };
    // User cancellation — don't retry.
    if (anyErr.name === 'AbortError') return false;
    // Our own timeout / stall watchdog — retry.
    if (anyErr.name === 'TimeoutError') return true;
    if (anyErr.status === 429) return true;
    if (typeof anyErr.status === 'number' && anyErr.status >= 500) return true;
    if (anyErr.code === 'ECONNRESET' || anyErr.code === 'ETIMEDOUT') return true;
    if (err instanceof TypeError) return true; // browser fetch network failure
  }
  return false;
};

function sleepWithAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    let listener: (() => void) | undefined;
    const tid = setTimeout(() => {
      if (signal && listener) signal.removeEventListener('abort', listener);
      resolve();
    }, ms);
    if (signal) {
      listener = () => {
        clearTimeout(tid);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      signal.addEventListener('abort', listener, { once: true });
    }
  });
}

export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const attempts = opts.attempts ?? 4;
  const base = opts.baseDelayMs ?? 500;
  const maxDelay = opts.maxDelayMs ?? 10_000;
  const shouldRetry = opts.shouldRetry ?? defaultShouldRetry;
  const label = opts.label ?? 'retry';
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    if (opts.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i === attempts - 1 || !shouldRetry(err)) throw err;
      const exp = base * Math.pow(2, i);
      // ±30% jitter so concurrent retries don't pile up in lockstep.
      const jittered = exp * (0.7 + Math.random() * 0.6);
      const delay = Math.min(jittered, maxDelay);
      const msg = (err as Error)?.message ?? String(err);
      console.warn(
        `[${label}] attempt ${i + 1}/${attempts} failed: ${msg} — retrying in ${Math.round(delay)}ms`
      );
      await sleepWithAbort(delay, opts.signal);
    }
  }
  throw lastErr;
}
